import { baseApi } from "./api";

export const projectMembersApi = baseApi.injectEndpoints({
	endpoints: (build) => ({
		getProjectMembers: build.query({
			query: (projectId) => ({
				url: `projects/${projectId}/members`,
			}),
			providesTags: (result, _e, projectId) => [
				{ type: "ProjectMember", id: projectId },
				...((result?.items ?? []).map((m) => ({
					type: "ProjectMember",
					id: m.id,
				}))),
			],
		}),
		getCurrentMember: build.query({
			query: ({ projectId }) => ({
				url: "project-members/me",
				params: { projectId },
			}),
			providesTags: (_result, _error, { projectId }) => [
				{ type: "ProjectMember", id: projectId },
			],
		}),
		getUserByEmail: build.query({
			// Called with the raw email string (see tab-external-guest.jsx).
			query: (email) => ({
				url: "users/lookup",
				params: { email },
			}),
		}),
		// Partial, case-insensitive email search for the invite-wizard typeahead.
		searchUsersByEmail: build.query({
			query: ({ search, limit = 10 }) => ({
				url: "users/search",
				params: { search, limit },
			}),
		}),
		inviteProjectMembers: build.mutation({
			query: (body) => ({
				url: "project-members/invite",
				method: "POST",
				body,
			}),
			invalidatesTags: (_r, _e, { projectId }) => [
				{ type: "ProjectMember", id: projectId },
			],
		}),
		inviteProvisionalMember: build.mutation({
			query: ({ projectId, memberId, ...body }) => ({
				url: `projects/${projectId}/members/${memberId}/invite`,
				method: "POST",
				body,
			}),
			invalidatesTags: (_r, _e, { projectId, memberId }) => [
				{ type: "ProjectMember", id: projectId },
				{ type: "ProjectMember", id: memberId },
			],
		}),
		updateMemberPermissions: build.mutation({
			query: ({ projectId, memberId, ...body }) => ({
				url: `projects/${projectId}/members/${memberId}/permissions`,
				method: "PUT",
				body,
			}),
			invalidatesTags: (_r, _e, { projectId, memberId }) => [
				{ type: "ProjectMember", id: projectId },
				{ type: "ProjectMember", id: memberId },
			],
		}),
		promoteAdmin: build.mutation({
			// Promotion sends only the flag (backend stores no permission blob for
			// admins). Demotion must carry the replacement permissions chosen in
			// the demote popup.
			query: ({
				projectId,
				memberId,
				isProjectAdmin,
				permissions,
				presetUsedName,
			}) => ({
				url: `projects/${projectId}/members/${memberId}/promote-admin`,
				method: "POST",
				body: {
					isProjectAdmin,
					...(permissions !== undefined ? { permissions } : {}),
					...(presetUsedName !== undefined ? { presetUsedName } : {}),
				},
			}),
			invalidatesTags: (_r, _e, { projectId, memberId }) => [
				{ type: "ProjectMember", id: projectId },
				{ type: "ProjectMember", id: memberId },
			],
		}),
		assignProjectAdmin: build.mutation({
			query: ({ projectId, userId }) => ({
				url: `projects/${projectId}/assign-admin`,
				method: "POST",
				body: { userId },
			}),
			invalidatesTags: (_result, _error, { projectId }) => [
				{ type: "ProjectMember", id: projectId },
			],
		}),
		removeProjectMember: build.mutation({
			query: ({ projectId, memberId }) => ({
				url: `projects/${projectId}/members/${memberId}`,
				method: "DELETE",
			}),
			invalidatesTags: (_r, _e, { projectId, memberId }) => [
				{ type: "ProjectMember", id: projectId },
				{ type: "ProjectMember", id: memberId },
			],
		}),
		revokeProjectMember: build.mutation({
			query: ({ projectId, memberId }) => ({
				url: `projects/${projectId}/members/${memberId}/revoke`,
				method: "POST",
			}),
			invalidatesTags: (_r, _e, { projectId, memberId }) => [
				{ type: "ProjectMember", id: projectId },
				{ type: "ProjectMember", id: memberId },
			],
		}),
		resendInvite: build.mutation({
			// Re-issues the invite token + email for an existing row that hasn't
			// been accepted yet (PENDING, or a re-invited uninvited row). Takes
			// no body - the backend reuses the row's current permissions/preset.
			query: ({ projectId, memberId }) => ({
				url: `projects/${projectId}/members/${memberId}/resend-invite`,
				method: "POST",
			}),
			invalidatesTags: (_r, _e, { projectId, memberId }) => [
				{ type: "ProjectMember", id: projectId },
				{ type: "ProjectMember", id: memberId },
			],
		}),
		acceptProjectInvite: build.mutation({
			query: ({ token }) => ({
				url: "project-members/accept-invite",
				method: "GET",
				params: { token },
			}),
			invalidatesTags: ["ProjectMember"],
		}),
	}),
});

export const {
	useGetProjectMembersQuery,
	useGetCurrentMemberQuery,
	useLazyGetUserByEmailQuery,
	useLazySearchUsersByEmailQuery,
	useInviteProjectMembersMutation,
	useInviteProvisionalMemberMutation,
	useUpdateMemberPermissionsMutation,
	usePromoteAdminMutation,
	useAssignProjectAdminMutation,
	useRemoveProjectMemberMutation,
	useRevokeProjectMemberMutation,
	useResendInviteMutation,
	useAcceptProjectInviteMutation,
} = projectMembersApi;

/** Alias used by invite wizard (PR #25). */
export { useLazyGetUserByEmailQuery as useLazyLookupUserByEmailQuery };
